import { AppLayout, Button, MenuType } from '@seiue/ui'
import { get, isArray, noop } from '@seiue/util'
import { ErrorBoundary } from 'packages/components/ErrorBoundary'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import { RouteCrumb } from 'packages/components/Page/Breadcrumbs'
import React, { useEffect, useRef, useState } from 'react'
import { useIsFetching } from 'react-query'
import styled from 'styled-components'

import { LayoutContext } from './context'
import type { MenuItemUnion } from './types'

export interface LayoutProps {
  /**
   * Header 左侧 slot
   */
  HeaderLeft?: React.ComponentType<any> | React.ReactElement | null
  /**
   * Header 右侧 slot
   */
  HeaderRight?: React.ComponentType<any> | React.ReactElement | null
  /**
   * 侧边栏菜单标题
   */
  siderMenuTitle?: string
  /**
   * 侧边栏菜单定义
   */
  menus: MenuItemUnion[] | MenuItemUnion
  /**
   * 侧边栏底部 slot
   */
  siderFooter?: React.ReactNode
  /**
   * 当前应激活的菜单所对应的 path
   */
  activeMenuPath: string
  /**
   * 顶部导航背景色
   */
  navbarBackgroundColor?: string
  /**
   * 侧边栏背景色
   */
  sliderBackgroundColor?: string
  /**
   * logoSlot, 不传则默认展示希悦 logo
   */
  logoSlot?: React.ReactNode
  /**
   * 自定义面包屑
   */
  formatBreadcrumbs?: (breadcrumbs: RouteCrumb[]) => RouteCrumb[]
  /**
   * 当前显示菜单类型发生了变更
   */
  onDisplayMenuTypesChanged?: (types: string[]) => void
}

/**
 * growp 使用的 layout
 *
 * @param props - props
 * @returns component
 */
export const WebLayout: React.FC<
  LayoutProps & { children?: React.ReactNode }
> = (props) => {
  const {
    HeaderLeft,
    HeaderRight,
    siderMenuTitle,
    menus,
    activeMenuPath,
    children,
    siderFooter,
    navbarBackgroundColor,
    sliderBackgroundColor = '#3B404B',
    logoSlot,
    formatBreadcrumbs,
  } = props

  //   const history = useHistory()

  const [isCollapsed, setIsCollapsed] = useState(false)
  //   const isAdminMode = history.location.pathname.startsWith("/admin/")

  //   const [openKeys, setOpenKeys] = React.useState<string[]>([])

  const contentContainerRef = useRef(null)

  const onRouteChangeRef = useRef(noop)

  useEffect(() => {
    onRouteChangeRef.current()
  }, [menus])

  const finalMenus = React.useMemo(
    () => (isArray(menus) ? menus : get(menus, 'subMenus') || []),
    [menus]
  )

  return (
    <LayoutContext.Provider
      value={{
        collapsed: isCollapsed,
        contentContainerEl: contentContainerRef.current,
        activeMenuPath,
        setIsCollapsed,
        formatBreadcrumbs,
      }}
    >
      <AppLayoutWrapper>
        <AppLayout
          showCollapsedTrigger={false}
          menus={finalMenus as MenuType[]}
          activeMenuPath={activeMenuPath}
          logoSlot={logoSlot}
          isCollapsed={isCollapsed}
          onIsCollapsedChange={setIsCollapsed}
          siderMenuTitle={siderMenuTitle}
          siderFooter={
            siderFooter || (
              <Collapsed onClick={() => setIsCollapsed(!isCollapsed)}>
                {
                  <GrowpIcon
                    name={isCollapsed ? 'Expand' : 'Fold'}
                    size={20}
                    color="#718999"
                  />
                }
              </Collapsed>
            )
          }
          headerLeft={HeaderLeft}
          headerRight={HeaderRight}
          // onOpenKeysChanged={setOpenKeys}
          navbarBackgroundColor={navbarBackgroundColor}
          sliderBackgroundColor={sliderBackgroundColor}
          showLoadingBar={!!useIsFetching()}
          headerClassName="se-web-layout__header"
          siderClassName="se-web-layout__sider"
        >
          <ContentContainer
            className="se-web-layout__container"
            ref={contentContainerRef}
          >
            <ErrorBoundary>{children}</ErrorBoundary>
          </ContentContainer>
        </AppLayout>
      </AppLayoutWrapper>
    </LayoutContext.Provider>
  )
}

const ContentContainer = styled.div`
  position: relative;
  flex-grow: 1;
  overflow-y: auto;
  background-color: ${(props) => props.theme.background._3};
`

const AppLayoutWrapper = styled.div`
  &&& {
    .se-web-layout__header {
      padding: 0 20px !important;
      height: 64px !important;
      z-index: 10;
      box-shadow: 0px 0px 1px 0px rgba(21, 29, 41, 0.2),
        0px 3px 8px -1px rgba(21, 29, 41, 0.04) !important;
    }
    .se-web-layout__container {
      & > div {
        padding: 0 20px 20px 20px;
        min-width: 1050px;
      }
    }
    .se-web-layout__sider {
      z-index: 0 !important;
      max-width: 200px !important;
      min-width: 57px !important;
      width: 200px !important;
    }
    .ant-menu {
      padding: 0 8px;
    }
    .ant-menu-item {
      border-radius: 8px;
    }
    .ant-menu-item-selected {
      background-color: ${(props) => props.theme.brand._5} !important;
      border-radius: 8px;
    }
    .ant-layout-sider-children > div {
      padding-top: 1px;
    }
    // 侧边栏选中的菜单样式
    .ant-menu-item-selected {
      .icon-wrapper > span {
        color: ${(props) => props.theme.brand._1} !important;
      }
      .icon-wrapper path {
        fill: ${(props) => props.theme.brand._1} !important;
      }
    }
    .se-button__link,
    .se-button__special {
      color: ${(props) => props.theme.brand._1};
      &:hover {
        color: ${(props) => props.theme.brand._2};
      }
    }
    .se-button__special {
      border-color: ${(props) => props.theme.brand._1};
    }
  }
`

const Collapsed = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background-color: #f5fafa;
  float: right;
  margin-right: 12px;
  margin-bottom: 12px;
  cursor: pointer;
  &:hover {
    background-color: ${(props) => props.theme.background._5};
  }
`
